import React from 'react';
import { Handle, Position } from 'reactflow';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui';

// Nested format from MCP server
interface NestedAssertionData {
  expression?: string;
  message?: string;
  severity?: 'error' | 'warning' | 'info';
}

interface AssertionNodeData {
  label?: string;
  // Support both flat format (assertion: string) and nested format (assertion: { expression, message, severity })
  assertion?: string | NestedAssertionData;
  errorMessage?: string;
  continueOnFailure?: boolean;
}

// Helper to extract assertion expression from either format
function getAssertionExpression(assertion: string | NestedAssertionData | undefined): string {
  if (!assertion) return '';
  if (typeof assertion === 'string') return assertion;
  if (typeof assertion === 'object' && assertion.expression) return assertion.expression;
  return '';
}

// Helper to extract error message from either format
function getAssertionMessage(data: AssertionNodeData): string {
  // Check errorMessage first (flat format)
  if (data.errorMessage && typeof data.errorMessage === 'string') return data.errorMessage;
  // Check nested format
  if (data.assertion && typeof data.assertion === 'object' && data.assertion.message) {
    return data.assertion.message;
  }
  return '';
}

// Helper to check if assertion should continue on failure
function shouldContinueOnFailure(data: AssertionNodeData): boolean {
  // Check flat format first
  if (typeof data.continueOnFailure === 'boolean') return data.continueOnFailure;
  // Check nested severity (warning = continue, error = stop)
  if (data.assertion && typeof data.assertion === 'object' && data.assertion.severity) {
    return data.assertion.severity !== 'error';
  }
  return false;
}

export default function AssertionNode({ 
  data, 
  selected 
}: { 
  data: AssertionNodeData; 
  selected?: boolean 
}) {
  return (
    <div className={cn(
      "min-w-[160px] rounded-lg",
      "bg-card",
      "border-2 transition-all duration-fast",
      selected 
        ? "border-accent-green shadow-glow-focus"
        : "border-accent-green/50 dark:border-accent-green/30 shadow-md hover:shadow-lg"
    )}>
      {/* Connection Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !border-2 !bg-accent-green !border-accent-green"
        style={{ top: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border dark:border-border-subtle">
        <div className="w-6 h-6 rounded bg-accent-green/10 dark:bg-accent-green/20 flex items-center justify-center">
          <CheckCircle className="w-3.5 h-3.5 text-accent-green" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Assertion
          </div>
          <div className="text-xs text-muted-foreground/70">
            {shouldContinueOnFailure(data) ? 'Soft Check' : 'Hard Check'}
          </div>
        </div>
        <Badge
          variant={shouldContinueOnFailure(data) ? 'outline' : 'destructive'}
          className={shouldContinueOnFailure(data) ? 'border-accent-yellow text-accent-yellow' : ''}
        >
          {shouldContinueOnFailure(data) ? 'Warn' : 'Fail'}
        </Badge>
      </div>

      {/* Node Body */}
      <div className="p-3 space-y-2">
        {getAssertionExpression(data.assertion) && (
          <div className="text-xs font-mono bg-muted p-2 rounded border border-border dark:border-border-subtle">
            <div className="text-foreground truncate">
              {getAssertionExpression(data.assertion)}
            </div>
          </div>
        )}

        {getAssertionMessage(data) && (
          <div className="flex items-start gap-1 text-xs text-accent-red">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>{getAssertionMessage(data)}</span>
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !bg-accent-green !border-accent-green"
        style={{ bottom: '-6px' }}
      />
    </div>
  );
}

